import { useState, useEffect, useMemo, useCallback } from "react";
import { ChevronDown, Cpu, Star } from "lucide-react";
import { cn } from "../../lib/utils";
import { api } from "../../api";

export interface Machine {
  id: number;
  code: string;
  name: string;
  priceUsd: number;
  /** Live open/closed from CollectorCrypt. The API only returns public
   *  machines, so a closed one here is listed on the site but currently
   *  offline — show it as such rather than hiding it. */
  status: string;
  /** CollectorCrypt's menu grouping ("Pokemon", "Sports", …). null → "Others". */
  category: string | null;
}

interface MachineSelectorProps {
  value: string | null;
  onChange: (machineCode: string) => void;
  className?: string;
}

const STORAGE_KEY = "cc_selected_machine";
const FAVORITES_KEY = "cc_favorite_machines";
const CATEGORY_KEY = "cc_machine_category";
const ALL = "__all__";
const OTHER = "Others"; // matches CollectorCrypt's own menu label

// localStorage can throw (private mode, blocked storage) — never let a
// convenience preference take the selector down.
function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeStorage(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

export function useMachineSelection() {
  const [selectedMachine, setSelectedMachine] = useState<string | null>(() =>
    typeof window !== "undefined" ? readStorage(STORAGE_KEY) : null
  );

  const selectMachine = (machineCode: string) => {
    setSelectedMachine(machineCode);
    writeStorage(STORAGE_KEY, machineCode);
  };

  return { selectedMachine, selectMachine };
}

function useFavoriteMachines() {
  const [favorites, setFavorites] = useState<Set<string>>(() => {
    const raw = readStorage(FAVORITES_KEY);
    if (!raw) return new Set();
    try {
      const parsed: unknown = JSON.parse(raw);
      return new Set(
        Array.isArray(parsed) ? parsed.filter((c) => typeof c === "string") : []
      );
    } catch {
      return new Set();
    }
  });

  const toggleFavorite = useCallback((code: string) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      writeStorage(FAVORITES_KEY, JSON.stringify([...next]));
      return next;
    });
  }, []);

  return { favorites, toggleFavorite };
}

function OfflineBadge({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide bg-amber-500/15 text-amber-400",
        className
      )}
    >
      Offline
    </span>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="sticky top-0 z-10 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground bg-card/95 backdrop-blur-sm border-b border-border/60">
      {children}
    </div>
  );
}

function MachineRow({
  machine,
  selected,
  favorite,
  onSelect,
  onToggleFavorite,
}: {
  machine: Machine;
  selected: boolean;
  favorite: boolean;
  onSelect: () => void;
  onToggleFavorite: () => void;
}) {
  const offline = machine.status !== "open";
  return (
    <div
      className={cn(
        "flex items-stretch hover:bg-secondary/50 transition-colors",
        selected && "bg-secondary/30",
        offline && "opacity-60"
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        className="flex-1 min-w-0 px-3 py-2.5 text-left flex items-center justify-between gap-2"
      >
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{machine.name}</div>
          <div className="text-xs text-muted-foreground">${machine.priceUsd}</div>
        </div>
        {offline && <OfflineBadge />}
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavorite();
        }}
        aria-pressed={favorite}
        aria-label={favorite ? `Unpin ${machine.name}` : `Pin ${machine.name}`}
        title={favorite ? "Unpin from top" : "Pin to top"}
        className={cn(
          "shrink-0 px-3 flex items-center transition-colors",
          favorite
            ? "text-amber-400 hover:text-amber-300"
            : "text-muted-foreground/40 hover:text-muted-foreground"
        )}
      >
        <Star className={cn("h-4 w-4", favorite && "fill-current")} />
      </button>
    </div>
  );
}

export function MachineSelector({ value, onChange, className }: MachineSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [category, setCategory] = useState<string>(
    () => readStorage(CATEGORY_KEY) ?? ALL
  );
  const { favorites, toggleFavorite } = useFavoriteMachines();

  useEffect(() => {
    async function fetchMachines() {
      try {
        const response = await api.stats.machines.get();
        if (response.data && "data" in response.data) {
          const machineList = response.data.data as Machine[];
          setMachines(machineList);

          // Pick a machine when none is selected, OR when the current
          // selection is no longer available (e.g. a machine that was
          // unlisted upstream but still lingers in localStorage).
          if (machineList.length > 0) {
            const isCurrentValid =
              value != null && machineList.some((m) => m.code === value);
            if (!isCurrentValid) {
              const stored = readStorage(STORAGE_KEY);
              const machineToSelect =
                machineList.find((m) => m.code === stored) ?? machineList[0];
              if (machineToSelect) {
                onChange(machineToSelect.code);
              }
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch machines:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchMachines();
  }, [value, onChange]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen]);

  const categoryOf = (m: Machine) => m.category ?? OTHER;

  // Categories in order of first appearance (machines arrive in display
  // order, so this mirrors the site's menu). "Other" always sinks to the end.
  const categories = useMemo(() => {
    const seen: string[] = [];
    for (const m of machines) {
      const c = categoryOf(m);
      if (!seen.includes(c)) seen.push(c);
    }
    return seen.sort((a, b) => (a === OTHER ? 1 : b === OTHER ? -1 : 0));
  }, [machines]);

  // A stored category that no longer exists falls back to All.
  const activeCategory =
    category === ALL || categories.includes(category) ? category : ALL;

  const selectCategory = (c: string) => {
    setCategory(c);
    writeStorage(CATEGORY_KEY, c);
  };

  const filtered =
    activeCategory === ALL
      ? machines
      : machines.filter((m) => categoryOf(m) === activeCategory);
  const pinned = filtered.filter((m) => favorites.has(m.code));
  const rest = filtered.filter((m) => !favorites.has(m.code));

  // Under "All", group the unpinned list by category so it scans like the
  // site menu. Under a single category, headers would just be noise.
  const groups: Array<{ label: string | null; items: Machine[] }> =
    activeCategory === ALL
      ? categories
          .map((c) => ({ label: c, items: rest.filter((m) => categoryOf(m) === c) }))
          .filter((g) => g.items.length > 0)
      : [{ label: null, items: rest }];

  const selectedMachine = machines.find((m) => m.code === value);
  const showChips = categories.length > 1;

  const renderRow = (machine: Machine) => (
    <MachineRow
      key={machine.id}
      machine={machine}
      selected={value === machine.code}
      favorite={favorites.has(machine.code)}
      onSelect={() => {
        onChange(machine.code);
        setIsOpen(false);
      }}
      onToggleFavorite={() => toggleFavorite(machine.code)}
    />
  );

  return (
    <div className={cn("relative min-w-0", className)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors disabled:opacity-50 min-w-0 overflow-hidden"
      >
        <Cpu className="h-4 w-4 shrink-0" />
        <span className="text-sm font-medium truncate min-w-0 flex-1 text-left">
          {isLoading
            ? "Loading..."
            : selectedMachine
              ? selectedMachine.name
              : "Select Machine"}
        </span>
        {selectedMachine && selectedMachine.status !== "open" && <OfflineBadge />}
        <ChevronDown className={cn("h-4 w-4 shrink-0 transition-transform", isOpen && "rotate-180")} />
      </button>

      {isOpen && !isLoading && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          {/* On phones the trigger shares a row with the time-range picker, so
              anchor right and stretch to the header's content width
              (100vw minus the 16px gutters) instead of the trigger's width. */}
          <div className="absolute right-0 top-full mt-1 z-50 w-[calc(100vw-2rem)] sm:w-auto sm:min-w-[280px] rounded-lg border border-border bg-card shadow-lg overflow-hidden">
            <div className="border-b border-border">
              <p className="px-4 pt-2 pb-1 text-xs text-muted-foreground">
                Select a gacha machine
              </p>
              {showChips && (
                <div
                  role="tablist"
                  aria-label="Filter by category"
                  className="flex gap-1.5 px-3 pb-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                  {[ALL, ...categories].map((c) => {
                    const active = c === activeCategory;
                    return (
                      <button
                        key={c}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => selectCategory(c)}
                        className={cn(
                          "shrink-0 rounded-full px-2.5 py-1 text-xs font-medium border transition-colors",
                          active
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-transparent text-muted-foreground border-border hover:text-foreground hover:border-foreground/40"
                        )}
                      >
                        {c === ALL ? "All" : c}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="max-h-[min(60vh,380px)] overflow-y-auto overscroll-contain">
              {pinned.length > 0 && (
                <>
                  <SectionLabel>
                    <span className="inline-flex items-center gap-1">
                      <Star className="h-3 w-3 fill-current text-amber-400" />
                      Pinned
                    </span>
                  </SectionLabel>
                  {pinned.map(renderRow)}
                </>
              )}

              {groups.map((g, i) => (
                <div key={g.label ?? `group-${i}`}>
                  {g.label && <SectionLabel>{g.label}</SectionLabel>}
                  {g.items.map(renderRow)}
                </div>
              ))}

              {filtered.length === 0 && (
                <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                  No machines in this category
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
